import { Box, Card, Link, Stack, Tab, Tabs, Typography, useMediaQuery } from '@mui/material'
import { useState } from 'react'
import { CardImg, CardImgMobile, CompletionCardSx, TypTitle } from './style'
import { SocialOrderInfo } from '../SocialOrderInfo'
import { SocialOrderProjectsSection } from '../SocialOrderProjectsSection'
import { PureLightTheme } from '../../../styles/theme'
import { SocialOrderDescriptionProps } from '../../../types/entitys/socialOrders'
import { TabsStyle } from '../../ProjectsPage/ProjectsTabs/style'
import { EntityImagePreviewModal } from '../../common/EntityImagePreviewModal'
import { useEntityImagePreview } from '../../common/useEntityImagePreview'
import { ImageGalleryModal } from '../../ImageGalleryModal'
import { IconImagesLeft } from '../../../icons/IconImagesLeft'
import { IconImagesRight } from '../../../icons/IconImagesRight'
import {
  BoxTabs,
  ImageItem,
  ImageTab,
  TabsStyle as GalleryTabsStyle,
} from '../../ProjectPage/Sections/Description/style'

function isSafeHttpUrl(url: string): boolean {
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

export const SocialOrderDescription = ({ socialOrder }: SocialOrderDescriptionProps) => {
  const isTablet = useMediaQuery(PureLightTheme.breakpoints.down('md'))
  const [currentTab, setCurrentTab] = useState(0)
  const {
    canPreview,
    openPreview,
    closePreview,
    galleryOpen: coverGalleryOpen,
    galleryImages: coverGalleryImages,
  } = useEntityImagePreview(socialOrder.image)
  const problem = socialOrder.problem?.trim() ?? ''
  const completionDescription = socialOrder.completion_description?.trim() ?? ''
  const completionResourceUrl = socialOrder.completion_resource_url?.trim() ?? ''
  const showCompletionCard = socialOrder.is_completed === true
  const showProblemCard = problem.length > 0
  const safeResourceUrl =
    completionResourceUrl && isSafeHttpUrl(completionResourceUrl) ? completionResourceUrl : null
  const linkedProjectsTotal = Number(socialOrder.counters?.linked_projects ?? 0)
  const previewSx = canPreview ? { ...CardImg, cursor: 'pointer' as const } : CardImg
  const previewSxMobile = canPreview
    ? { ...CardImgMobile, cursor: 'pointer' as const }
    : CardImgMobile

  const imageGallery = (socialOrder.image_gallery ?? []).filter((src) => Boolean(src?.trim()))
  const [galleryOpen, setGalleryOpen] = useState(false)
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(0)

  const openGallery = (index: number) => {
    setGalleryInitialIndex(index)
    setGalleryOpen(true)
  }

  return (
    <Box>
      <Stack direction="row" mb={2}>
        {socialOrder.image ? (
          <Box
            component="img"
            src={socialOrder.image}
            alt=""
            sx={previewSx}
            onClick={canPreview ? openPreview : undefined}
          />
        ) : (
          <Box sx={CardImg} />
        )}
        <Box>
          {socialOrder.image ? (
            <Box
              component="img"
              src={socialOrder.image}
              alt=""
              sx={previewSxMobile}
              onClick={canPreview ? openPreview : undefined}
            />
          ) : null}
          <Typography variant="h1" sx={TypTitle}>{socialOrder.title}</Typography>
        </Box>
      </Stack>

      {imageGallery.length > 0 && (
        <Box sx={BoxTabs} mb={2}>
          <Tabs
            value={false}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={GalleryTabsStyle}
            slots={{
              StartScrollButtonIcon: IconImagesLeft,
              EndScrollButtonIcon: IconImagesRight,
            }}
          >
            {imageGallery.map((src, index) => (
              <Tab
                key={`${src}-${index}`}
                disableRipple
                sx={ImageTab}
                onClick={() => openGallery(index)}
                icon={
                  <Box
                    component="img"
                    src={src}
                    alt=""
                    sx={ImageItem}
                  />
                }
              />
            ))}
          </Tabs>
        </Box>
      )}

      <Stack direction="column" gap={{ xs: 1.5, sm: 2 }} mb={2} sx={{ minWidth: 0 }}>
        <Tabs
          value={currentTab}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={TabsStyle}
          onChange={(_event, value) => setCurrentTab(value)}
        >
          <Tab label="Описание" />
          <Tab label="Проекты" />
        </Tabs>
      </Stack>

      {currentTab === 0 ? (
        <Stack direction="column" gap={2} sx={{ minWidth: 0 }}>
          {showCompletionCard ? (
            <Card variant="primaryAdaptiveCard" sx={CompletionCardSx}>
              <Typography variant="h3" mb={{ xs: 0.5, sm: 1 }}>
                Информация о выполнении
              </Typography>
              {completionDescription ? (
                <Typography variant="body1" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                  {completionDescription}
                </Typography>
              ) : null}
              {safeResourceUrl ? (
                <Typography variant="body1" component="div" mt={completionDescription ? 1 : 0}>
                  <Link
                    href={safeResourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    {safeResourceUrl}
                  </Link>
                </Typography>
              ) : null}
            </Card>
          ) : null}

          {isTablet && <SocialOrderInfo socialOrder={socialOrder} />}

          {showProblemCard ? (
            <Card variant="primaryAdaptiveCard" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              <Typography variant="h3" mb={{ xs: 0.5, sm: 1 }} sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                Проблема которую решает социальный заказ
              </Typography>
              <Typography
                variant="body1"
                component="div"
                sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word', minWidth: 0 }}
              >
                {problem}
              </Typography>
            </Card>
          ) : null}

          <Card variant="primaryAdaptiveCard" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            <Typography variant="h3" mb={{ xs: 0.5, sm: 1 }} sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
              Описание
            </Typography>
            <Typography
              variant="body1"
              component="div"
              sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word', minWidth: 0 }}
            >
              {socialOrder.description}
            </Typography>
          </Card>

        </Stack>
      ) : (
        <SocialOrderProjectsSection
          socialOrderId={socialOrder.id}
          total={linkedProjectsTotal}
        />
      )}
      <EntityImagePreviewModal
        open={coverGalleryOpen}
        onClose={closePreview}
        images={coverGalleryImages}
      />
      <ImageGalleryModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        images={imageGallery}
        initialIndex={galleryInitialIndex}
      />
    </Box>
  )
}
